import { useCallback, useRef, useState, useSyncExternalStore } from 'react';
import {
  ChevronDown,
  CircleHelp,
  CloudOff,
  Download,
  FileDown,
  FileText,
  FolderOpen,
  HardDriveDownload,
  Inbox,
  Printer,
  RotateCcw,
  Save,
  Trash2,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  IconButton,
  LogoLockup,
  Sheet,
  SheetTrigger,
  Spinner,
  ThemeToggle,
  Tooltip,
  badgeStyles,
  cn,
} from '@/components/ui';
import { UNIT_OPTIONS, isUnitsSpecified } from '@/components/common/units';
import { useAppStore } from '@/store/appStore';
import { useAutosaveStatus } from '@/store/autosaveStatus';
import { useProgressStore } from '@/store/progressStore';
import { HelpSheetContent } from './HelpSheet';
import { selectExportAvailability } from './exportAvailability';

export interface ExampleOption {
  key: string;
  name: string;
  description: string;
}

export interface HeaderProps {
  examples: ReadonlyArray<ExampleOption>;
  onLoadExample: (key: string) => void;
  onOpenProject: (file: File) => void;
  onSaveProject: () => void;
  onUndoReplacement: () => void;
  onReset: () => void;
  onExportCsv: () => void;
  onExportLog: () => void;
  onPrintReport: () => void;
  onCancelProcessing: () => void;
}

/** Wide enough to open the examples submenu beside the Project menu. */
const SUBMENU_QUERY = '(min-width: 640px)';

const useMediaQuery = (query: string): boolean => {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia?.(query);
      list?.addEventListener?.('change', onChange);
      return () => list?.removeEventListener?.('change', onChange);
    },
    [query]
  );
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia?.(query).matches ?? true,
    () => true
  );
};

const AUTOSAVE_COPY = {
  saved: {
    label: 'Autosaved',
    detail:
      'Saved in this browser. Save a project file to keep a complete copy.',
  },
  unavailable: {
    label: 'Autosave off',
    detail:
      'Browser storage is unavailable. Save a project file to keep your work.',
  },
} as const;

/** Units chip: shows the project units and changes them (labels only). */
const UnitsMenu = () => {
  const units = useAppStore((s) => s.units);
  const setUnits = useAppStore((s) => s.setUnits);
  const option = UNIT_OPTIONS.find((o) => o.value === units);
  const specified = isUnitsSpecified(units);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Project units: ${option?.label ?? units}`}
        className={badgeStyles({
          size: 'md',
          interactive: true,
          className: 'gap-1.5 font-normal',
        })}
      >
        Units
        <span
          className={cn('font-medium', specified ? 'text-fg' : 'text-fg-muted')}
        >
          {specified ? (option?.short ?? units) : 'not set'}
        </span>
        <ChevronDown aria-hidden="true" className="-mr-0.5 opacity-70" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60 print:hidden">
        <DropdownMenuLabel>Project units</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={units} onValueChange={setUnits}>
          {UNIT_OPTIONS.map((o) => (
            <DropdownMenuRadioItem key={o.value} value={o.value}>
              {o.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <p className="px-2 py-1.5 text-xs text-fg-muted">
          Labels only. Coordinates are never converted.
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Busy indicator with an always-reachable Cancel while a worker runs. */
const BusyIndicator = ({ onCancel }: { onCancel: () => void }) => {
  const isProcessing = useProgressStore((s) => s.isProcessing);
  const message = useProgressStore((s) => s.message);
  if (!isProcessing) return null;
  return (
    // Phones rely on the top progress bar and the delayed dialog's Cancel.
    <div className="hidden animate-fade-in items-center gap-1 rounded-full border border-border bg-surface-sunken py-0.5 pl-2.5 pr-0.5 [animation-delay:150ms] [animation-fill-mode:backwards] sm:flex">
      <Spinner size="sm" className="text-accent" />
      <span className="hidden max-w-[12rem] truncate text-xs text-fg-muted lg:inline">
        {message || 'Working…'}
      </span>
      <Button
        size="sm"
        variant="ghost"
        onClick={onCancel}
        aria-label="Cancel calculation"
        className="h-6 rounded-full px-2 text-xs"
      >
        Cancel
      </Button>
    </div>
  );
};

/**
 * Sticky top bar: brand lockup, project status (autosave, units), help,
 * theme, the Project menu, and the Export menu. Disabled menu items state
 * why they are unavailable.
 */
export const Header = ({
  examples,
  onLoadExample,
  onOpenProject,
  onSaveProject,
  onUndoReplacement,
  onReset,
  onExportCsv,
  onExportLog,
  onPrintReport,
  onCancelProcessing,
}: HeaderProps) => {
  const autosaveStatus = useAutosaveStatus((s) => s.status);
  const showHelp = useAppStore((s) => s.showHelp);
  const setShowHelp = useAppStore((s) => s.setShowHelp);
  const canUndoReplacement = useAppStore((s) => s.replacementSnapshot !== null);
  const hasData = useAppStore(
    (s) =>
      s.system1Points.length > 0 ||
      s.system2Points.length > 0 ||
      s.pointPairs.length > 0
  );
  const availability = useAppStore(useShallow(selectExportAvailability));
  const [confirmReset, setConfirmReset] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const projectTrigger = useRef<HTMLButtonElement>(null);
  // Import has its own units control; avoid showing two at once.
  const onImportStep = useAppStore((s) => s.activeTab === 'import');
  const autosave = AUTOSAVE_COPY[autosaveStatus];
  const canExportAnything = availability.report === null;
  const wideMenu = useMediaQuery(SUBMENU_QUERY);
  const exampleItems = examples.map((example) => (
    <DropdownMenuItem
      key={example.key}
      icon={wideMenu ? undefined : <Inbox />}
      description={example.description}
      onSelect={() => onLoadExample(example.key)}
    >
      {example.name}
    </DropdownMenuItem>
  ));

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-surface/90 backdrop-blur supports-[backdrop-filter]:bg-surface/75">
      <div className="mx-auto flex h-14 max-w-screen-2xl items-center gap-2 px-4 sm:gap-3 sm:px-6">
        <h1 className="flex">
          <LogoLockup bylineClassName="hidden sm:inline" />
        </h1>
        <Badge tone="accent" className="hidden px-1.5 sm:inline-flex">
          Beta
        </Badge>

        <div className="ml-1 hidden items-center gap-2 md:flex">
          <span aria-hidden="true" className="h-5 w-px bg-border" />
          <Tooltip content={autosave.detail}>
            <span
              role="status"
              tabIndex={0}
              className={badgeStyles({
                size: 'md',
                tone: autosaveStatus === 'saved' ? 'neutral' : 'warning',
                className: 'cursor-default gap-1.5 font-normal',
              })}
            >
              {autosaveStatus === 'saved' ? (
                <HardDriveDownload aria-hidden="true" />
              ) : (
                <CloudOff aria-hidden="true" />
              )}
              {autosave.label}
              <span className="sr-only">. {autosave.detail}</span>
            </span>
          </Tooltip>
          {!onImportStep && <UnitsMenu />}
        </div>

        <div className="ml-auto flex items-center gap-1 sm:gap-1.5">
          <BusyIndicator onCancel={onCancelProcessing} />

          <Sheet open={showHelp} onOpenChange={setShowHelp}>
            <SheetTrigger asChild>
              <IconButton aria-label="Help" icon={<CircleHelp />} />
            </SheetTrigger>
            <HelpSheetContent />
          </Sheet>
          <ThemeToggle />

          <input
            ref={fileInput}
            type="file"
            accept=".resolve,application/json"
            className="hidden"
            aria-hidden="true"
            tabIndex={-1}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onOpenProject(file);
              event.target.value = '';
            }}
          />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                ref={projectTrigger}
                aria-label="Project"
                variant="ghost"
                className="px-2.5 text-fg sm:px-3"
                trailingIcon={
                  <ChevronDown
                    aria-hidden="true"
                    className="hidden text-fg-muted sm:block"
                  />
                }
              >
                <FolderOpen aria-hidden="true" className="sm:hidden" />
                <span className="hidden sm:inline">Project</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 print:hidden">
              <p className="flex items-center gap-2 px-2 pb-1 pt-1.5 text-xs text-fg-muted md:hidden">
                {autosaveStatus === 'saved' ? (
                  <HardDriveDownload aria-hidden="true" className="size-3.5" />
                ) : (
                  <CloudOff aria-hidden="true" className="size-3.5" />
                )}
                {autosave.label}
              </p>
              <DropdownMenuItem
                icon={<FolderOpen />}
                onSelect={() => fileInput.current?.click()}
              >
                Open project…
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<Save />}
                disabled={!hasData}
                disabledReason="Nothing to save yet"
                onSelect={onSaveProject}
              >
                Save project
              </DropdownMenuItem>
              {wideMenu ? (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger icon={<Inbox />}>
                    Load example
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-64 print:hidden">
                    {exampleItems}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              ) : (
                // Phones have no room beside the menu for a submenu.
                <DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Load example</DropdownMenuLabel>
                  {exampleItems}
                </DropdownMenuGroup>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                icon={<RotateCcw />}
                disabled={!canUndoReplacement}
                disabledReason="Nothing to restore"
                onSelect={onUndoReplacement}
              >
                Undo replacement
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<Trash2 />}
                tone="danger"
                disabled={!hasData}
                disabledReason="The project is already empty"
                onSelect={() => setConfirmReset(true)}
              >
                Reset project…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant={canExportAnything ? 'primary' : 'secondary'}
                leadingIcon={<FileDown aria-hidden="true" />}
                trailingIcon={
                  <ChevronDown aria-hidden="true" className="opacity-80" />
                }
                className="px-3"
              >
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 print:hidden">
              <DropdownMenuItem
                icon={<Download />}
                description="Transformed points as PNEZD"
                disabled={availability.csv !== null}
                disabledReason={availability.csv}
                onSelect={onExportCsv}
              >
                Coordinates CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<FileText />}
                description="Plain-text record of the analysis"
                disabled={availability.report !== null}
                disabledReason={availability.report}
                onSelect={onExportLog}
              >
                Analysis log
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<Printer />}
                description="Print or save as PDF"
                disabled={availability.report !== null}
                disabledReason={availability.report}
                onSelect={onPrintReport}
              >
                Print report
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Dialog open={confirmReset} onOpenChange={setConfirmReset}>
        <DialogContent
          size="sm"
          title="Reset project?"
          description="This clears both datasets, all pairs, and results. You can undo until the next replacement or page reload."
          className="print:hidden"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            projectTrigger.current?.focus();
          }}
        >
          <DialogFooter>
            <DialogClose asChild>
              <Button>Cancel</Button>
            </DialogClose>
            <Button
              variant="danger"
              onClick={() => {
                setConfirmReset(false);
                onReset();
              }}
            >
              Reset project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
};
